import React, { useState, useCallback, useRef, useEffect } from 'react';
import SimpleBar from 'simplebar-react';
import classnames from 'classnames';
import { ContactListProps } from '../interfaces';
import ContactItem from './contactItem';
import { ContactListSkeleton } from '../Skeletons';
import { whatsappApi } from '../../api/whatsapp-api';
import { WhatsappContact } from '../../types/whatsapp';
import ContactFilterDropdown from "./ContactFilterDropdown";
import { useCustomerStates } from '../../../customer-states/hooks/useCustomerStates';

const ContactList: React.FC<ContactListProps> = ({
    contacts,
    contactsLoading,
    contactsHasMore,
    loadMoreContacts,
    selectedContactId,
    setSelectedContactId,
    unreadContacts,
    audioStatus,
    activateAudio,
    ignoreAudio,
    filterStateId,
    setFilterStateId
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<WhatsappContact[]>([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();

    const { allStates } = useCustomerStates();

    const handleSearch = useCallback(async (term: string) => {
        if (!term.trim()) {
            setSearchResults([]);
            setShowSearchResults(false);
            return;
        }

        try {
            const response = await whatsappApi.getContacts({ search: term.trim() });
            setSearchResults(response.data);
            setShowSearchResults(true);
        } catch (error) {
            console.error('Error searching contacts:', error);
            setSearchResults([]);
        }
    }, []);

    const debouncedSearch = useCallback((term: string) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            handleSearch(term);
        }, 300);
    }, [handleSearch]);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    const displayContacts = showSearchResults ? searchResults : contacts;

    const handleContactSelect = (contactId: string) => {
        setSelectedContactId(contactId);
    };

    return (
        <>
            {/* Header */}
            <div className="px-2 pt-4 pb-1 flex-shrink-0">
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <h5 className="mb-0">
                        <i className="ri-whatsapp-line text-success me-2"></i>
                        Chats
                    </h5>

                    <button
                        type="button"
                        className="btn btn-sm btn-ghost-secondary rounded-circle"
                        onClick={() => {
                            if (audioStatus === 'ALLOWED') ignoreAudio();
                            else activateAudio();
                        }}
                        title={audioStatus === 'ALLOWED' ? "Silenciar" : "Activar Sonido"}
                    >
                        <i className={classnames("fs-18", {
                            "ri-volume-up-line text-success": audioStatus === 'ALLOWED',
                            "ri-volume-mute-line text-muted": audioStatus !== 'ALLOWED'
                        })}></i>
                    </button>
                </div>

                <div className="w-100 pb-2 d-flex align-items-center gap-2">

                    <div
                        className={`d-flex align-items-center bg-light rounded-pill px-3 h-[40px] border flex-grow-1 ${
                            isFocused ? 'border-success' : 'border-light'
                        }`}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            height="18"
                            width="18"
                            fill="#667781"
                            className="flex-shrink-0"
                        >
                            <path d="M15.009 13.805h-.636l-.227-.217C15.59 12.222 16.53 10.45 16.53 8.515 16.53 4.364 13.166 1 9.015 1 4.863 1 1.5 4.364 1.5 8.515c0 4.151 3.363 7.515 7.515 7.515 1.935 0 3.707-.94 5.073-2.384l.217.227v.636l4.009 4.009 1.191-1.191-4.009-4.009zm-6.009 0c-2.932 0-5.315-2.383-5.315-5.315 0-2.932 2.383-5.315 5.315-5.315 2.932 0 5.315 2.383 5.315 5.315 0 2.932-2.383 5.315-5.315 5.315z" />
                        </svg>

                        <input
                            type="text"
                            placeholder="Busca..."
                            className="form-control form-control-sm border-0 bg-transparent shadow-none ms-2"
                            style={{
                                fontSize: '14px',
                                color: '#3b4a54',
                                outline: 'none'
                            }}
                            value={searchTerm}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchTerm(value);
                                debouncedSearch(value);
                            }}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => {
                                setIsFocused(false);
                            }}
                        />
                    </div>

                    <ContactFilterDropdown
                        filterStateId={filterStateId}
                        setFilterStateId={setFilterStateId}
                        allStates={allStates}
                    />

                </div>
            </div>

            {/* Contact list */}
            {contactsLoading && contacts.length === 0 ? (
                <ContactListSkeleton/>
            ) : (
                <div className="flex-grow-1 overflow-hidden" style={{minHeight: 0}}>
                    <SimpleBar className="h-100" style={{overflowX: 'hidden'}}>
                        <div className="chat-message-list">
                            <ul className="list-unstyled chat-list chat-user-list mb-0">
                                {displayContacts.map((contact) => (
                                    <ContactItem
                                        key={contact.id}
                                        contact={contact}
                                        isSelected={selectedContactId === contact.id}
                                        unreadCount={unreadContacts[contact.id] || 0}
                                        onSelect={handleContactSelect}
                                    />
                                ))}
                            </ul>

                            {contactsHasMore && (
                                <div className="text-center mb-3">
                                    <button
                                        className="btn btn-sm btn-light shadow-sm"
                                        onClick={loadMoreContacts}
                                        disabled={contactsLoading}
                                    >
                                        {contactsLoading ? (
                                            <span className="spinner-border spinner-border-sm me-1"
                                                  role="status"></span>
                                        ) : (
                                            <i className="ri-arrow-down-line me-1"></i>
                                        )}
                                        Cargar más contactos
                                    </button>
                                </div>
                            )}

                            <div className="py-1">
                                {contactsLoading && contacts.length > 0 && (
                                    <div className="text-center text-muted py-2">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Cargando…</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </SimpleBar>
                </div>
            )}
        </>
    );
};

export default ContactList;
